import { authFetch } from "./auth.js";
import { Notify } from "quasar";
import { env } from "@/env.js";

export const handleResponse = async (
    res,
    taskInfo = "Making a request",
    throwException = true,
) => {
    if (res.ok) {
        if (res.status == 204) {
            return res;
        }
        return res.clone().json();
    }
    let errorMessage = taskInfo;
    const errorResponse = await res.json();
    if (errorResponse.message) {
        if (res.status > 499) {
            errorMessage = `Network Error: ${errorMessage} ("${errorResponse.message}")`;
        } else {
            errorMessage = errorResponse.message;
        }
    }
    Notify.create({
        progress: true,
        type: res.status > 499 ? 'negative' : 'warning',
        message: errorMessage,
    });
    if (throwException) {
        let customError = new Error(
            `There was an error making a request: ${errorMessage}`,
        );
        Object.assign(customError, { response: errorResponse });
        throw customError;
    }
};

export const getAssets = async () => {
    const assetsResponse = await authFetch(
        `${env.VITE_BASE_API_URL}/assets`,
    ).then((res) => handleResponse(res, "Getting assets"));
    return assetsResponse;
};

// forecast routes
export const getLastForecast = async () => {
    const assetsResponse = await authFetch(
        `${env.VITE_BASE_API_URL}/forecast/last-update`,
    ).then((res) => handleResponse(res, "Getting last update time"));
    return assetsResponse;
};

export const getForecastModelStatus = async () => {
    const response = await authFetch(
        `${env.VITE_BASE_API_URL}/forecast/model-status`,
    ).then((res) => handleResponse(res, "Getting last update time"));
    return response;
};

export const getNewForecastAvailable = async (timestamp) => {
    const response = await authFetch(
        `${env.VITE_BASE_API_URL}/forecast/new-forecast-data-available?timestamp=${timestamp}`,
    ).then((res) => handleResponse(res, "Getting forecast updates"));
    return response;
};

// Settings
export const getBaseSettings = async () => {
    const settingsResponse = await authFetch(
        `${env.VITE_BASE_API_URL}/settings`,
    ).then((res) => handleResponse(res, "Getting user settings"));
    return settingsResponse;
};

export const getBaseOrganizationSettings = async () => {
    const settingsResponse = await authFetch(
        `${env.VITE_BASE_API_URL}/settings/organization`,
    ).then((res) => handleResponse(res, "Getting organization settings"));
    return settingsResponse;
};

export const updateUserSettings = async (userSettings) => {
    const userSettingsResponse = await authFetch(`${env.VITE_BASE_API_URL}/settings`, "PUT", {
        alertByEmail: userSettings["alertByEmail"],
        alertBySms: userSettings["alertBySms"],
        email: userSettings["email"],
        phoneNo: userSettings["phoneNo"],
        showGaugeScale: userSettings["showGaugeScale"],
        showRainScale: userSettings["showRainScale"],
        timezone: userSettings["timezone"],
        unitPreference: userSettings["unitPreference"],
    }).then((res) => handleResponse(res, "Updating user settings"));

    return userSettingsResponse;
};

export const updateForecastNotificationSettings = async (
    forecastNotificationSettings,
) => {
    await authFetch(
        `${env.VITE_BASE_API_URL}/settings/forecast-notification-settings`,
        "PUT",
        { forecastNotificationSettings },
    ).then((res) =>
        handleResponse(res, "Updating forecast notification settings"),
    );

    return true;
};

export const updateGaugeNotificationSettings = async (
    gaugeNotificationSettings,
) => {
    await authFetch(
        `${env.VITE_BASE_API_URL}/settings/gauge-notification-settings`,
        "PUT",
        { gaugeNotificationSettings },
    ).then((res) => handleResponse(res, "Updating gauge notification settings"));

    return true;
};

export const updateAssetSettings = async (changedAssets) => {
    await authFetch(
        `${env.VITE_BASE_API_URL}/settings/asset-risk-thresholds`,
        "PUT",
        {
            changedAssets: changedAssets,
        },
    ).then((res) => handleResponse(res, "Updating asset risk thresholds"));

    return true;
};

export const updateRiskSettings = async (userSettings) => {
    await authFetch(`${env.VITE_BASE_API_URL}/settings/organization`, "PUT", {
        forecastRiskLevels: userSettings["forecastLevels"],
        gaugeRiskLevels: userSettings["gaugeLevels"],
        updateOrganizationForecastRiskLevelDefaults: userSettings["forecastRpChanged"],
        updateOrganizationGaugeRiskLevelDefaults: userSettings["gaugeRpChanged"]
    }).then((res) => handleResponse(res, "Updating organization settings"));

    return true;
};

export const getForecastTableByAssetId = async (assetId) => {
    const forecastResponse = await authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/forecast-table`,
    ).then((res) => handleResponse(res, "Getting asset forecast"));
    return forecastResponse;
};

export const getForecastBarChartByAssetId = async (assetId, query = "") => {
    const forecastResponse = await authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/forecast-bar-chart${query}`,
    ).then((res) => handleResponse(res, "Getting asset forecast bar chart"));
    return forecastResponse;
};

export const getAssetReportMetadata = async(assetId, timestamp) => {
    const response = await authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/report-metadata?timestamp=${timestamp}`
    ).then((res) => handleResponse(res, "Getting report metadata"));
    return response;
}

export const getForecastCsv = async (
    assetId,
    resolution = 1,
    timestamp = "",
) => {
    const response = await authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/forecast-csv?resolution=${resolution}${timestamp ? `&timestamp=${timestamp}` : ""}`,
    );
    return response;
};

export const getForecastsForListOfAssetIds = async (assetIds) => {
    const forecastResponse = await authFetch(
        `${env.VITE_BASE_API_URL}/assets/forecast-table-batch?assetIds=${assetIds}`,
    ).then((res) => handleResponse(res, "Getting asset forecast"));
    return forecastResponse;
};

export const getReturnPeriodsByAssetId = async (assetId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/return-periods`,
    ).then((res) => handleResponse(res, "Getting return periods for an asset"));
};
export const getGaugeReturnPeriodsByAssetId = async (assetId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/gauge-risk-levels`,
    ).then((res) =>
        handleResponse(res, "Getting gauge return periods for an asset"),
    );
};

export const getVirtualGaugeReturnPeriodsByAssetId = async (assetId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/virtual-gauge-risk-levels`,
    ).then((res) =>
        handleResponse(res, "Getting virtual gauge return periods for an asset"),
    );
};

export const getBasinConditionsByAssetId = async (assetId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/basin-conditions`,
    ).then((res) => handleResponse(res, "Getting basin conditions for an asset"));
};

export const getAllAssetWatersheds = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/assets/watersheds`).then((res) =>
        handleResponse(res, "Getting all asset watersheds"),
    );
};

export const getWatershedByAssetId = async (assetId) => {
    return authFetch(`${env.VITE_BASE_API_URL}/assets/${assetId}/watershed`).then(
        (res) => handleResponse(res, "Getting a watershed for an asset"),
    );
};

// Gauge API functions
export const getAllGauges = async (lat, lng) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/gauges?latitude=${lat}&longitude=${lng}`,
    ).then((res) => handleResponse(res, ""));
};

export const getAllGaugesByDistance = async (lat, lng) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/layers/gauges/sorted-by-distance?latitude=${lat}&longitude=${lng}`,
    ).then((res) => handleResponse(res, ""));
};

export const getGaugeWatershedById = async (gaugeId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/gauges/${gaugeId}/gauge-watershed`,
    ).then((res) => handleResponse(res, "Getting a gauge's watershed"));
};

export const getVirtualGaugeWatershedById = async (gaugeId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/virtual-gauges/${gaugeId}/virtual-gauge-watershed`,
    ).then((res) => handleResponse(res, "Getting a gauge's watershed"));
};

export const getGaugeDataByGaugeId = async (gaugeId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/gauges/${gaugeId}/gauge-data`,
    ).then((res) => handleResponse(res, "Getting gauge data"));
};

export const getGaugeMetaDataByGaugeId = async (gaugeId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/gauges/${gaugeId}/gauge-metadata`,
    ).then((res) => handleResponse(res, "Getting gauge data"));
};

export const getVirtualGaugeDataByGaugeId = async (gaugeId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/virtual-gauges/${gaugeId}/virtual-gauge-data`,
    ).then((res) => handleResponse(res, "Getting gauge data"));
};

export const getVirtualGaugeMetaDataByGaugeId = async (gaugeId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/virtual-gauges/${gaugeId}/virtual-gauge-metadata`,
    ).then((res) => handleResponse(res, "Getting gauge data"));
};

export const getGaugeDataByAssetId = async (
    assetId,
    includeDailyRiskLevels = false,
) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/gauge-data${includeDailyRiskLevels ? "?includeDailyRiskLevels=true" : ""}`,
    ).then((res) => handleResponse(res, "Getting gauge data for an asset"));
};

export const getVirtualGaugeDataByAssetId = async (
    assetId,
    includeDailyRiskLevels = false,
) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/virtual-gauge-data${includeDailyRiskLevels ? "?includeDailyRiskLevels=true" : ""}`,
    ).then((res) => handleResponse(res, "Getting gauge data for an asset"));
};

// Report API functions
export const getReportDatesByAssetId = async (assetId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/forecast-dates`,
    ).then((res) => handleResponse(res, "Getting report dates"));
};

export const getAssetForecastTimesByDate = async (assetId, dateString) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/forecast-date?date=${dateString}`,
    ).then((res) => handleResponse(res, "Getting report dates"));
};

export const getReportForecastByAssetId = async (
    assetId,
    timestamp,
    throwException = true,
) => {
    const forecastResponse = await authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/report?${new URLSearchParams({ timestamp })}`,
    ).then((res) => handleResponse(res, "", throwException));
    return forecastResponse;
};

export const getReportBasinConditionsByAssetId = async (assetId, timestamp) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/basin-conditions?${new URLSearchParams({ timestamp })}`,
    ).then((res) => handleResponse(res, "Getting basin conditions for a report"));
};

export const getGaugeReportDataByAssetId = async (
    assetId,
    timestamp,
    throwException = true,
) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/gauge-report-data?${new URLSearchParams({ timestamp })}`,
    ).then((res) =>
        handleResponse(
            res,
            "Getting gauge report data for an asset",
            throwException,
        ),
    );
};

export const getVirtualGaugeReportDataByAssetId = async (
    assetId,
    timestamp,
    throwException = true,
) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/virtual-gauge-report-data?${new URLSearchParams({ timestamp })}`,
    ).then((res) =>
        handleResponse(
            res,
            "Getting gauge report data for an asset",
            throwException,
        ),
    );
};

export const getGaugeReportDates = async (assetId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/gauge-report-dates`,
    ).then((res) => res.json());
};

export const getVirtualGaugeReportDates = async (assetId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${assetId}/virtual-gauge-report-dates`,
    ).then((res) => res.json());
};

export const getLogReport = async (assetIds, startDate, endDate) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/risk-level-log?${new URLSearchParams({ assetIds, startDate, endDate })}`,
    ).then((res) => res.json());
};

// Settings API functions
export const getOrganizationDetails = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/organization-details`).then(
        (res) => handleResponse(res, "Getting organization details"),
    );
};

export const getOverlayGeoJSON = async () => {
    return await authFetch(
        `${env.VITE_BASE_API_URL}/settings/organization/map-overlay`,
    ).then((res) => handleResponse(res, "Getting the organization map overlay"));
};

export const putOverlayDetails = async (id, name, color) => {
    return await authFetch(
        `${env.VITE_BASE_API_URL}/admin/map-overlay/${id}`,
        "PUT",
        { name, color },
    ).then((res) => handleResponse(res, "Getting the organization map overlay"));
};

export const postOrganizationOverlay = async (overlay, name, color) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/map-overlay`, "POST", {
        geojson: overlay,
        name,
        color,
    }).then((res) => handleResponse(res, "Saving the organization map overlay"));
};

export const deleteOrganizationOverlay = async (id) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/map-overlay/${id}`,
        "DELETE",
    );
};

export const inviteUserRequest = async (body) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/users`, "POST", body).then(
        (res) => handleResponse(res, "Creating a user"),
    );
};

export const deleteUserRequest = async (user_id) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/users/${user_id}`, "DELETE");
};

// Feature Flag functions
export const getFeatureFlags = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/feature-flags`).then((res) =>
        handleResponse(res, "Getting feature flags"),
    );
};

// get active notifications
export const getActiveNotifications = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin-notification`).then((res) =>
        handleResponse(res, "Getting active administrative notifications"),
    );
};


// Asset Creation Functions
export const getWatershedJob = async (id) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/wfi/${id}`, "POST").then(
        (res) => handleResponse(res, "Creating a watershed job"),
    );
};

export const checkWatershedJob = async (id) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/wfi/${id}`).then((res) =>
        handleResponse(res, "Getting watershed job information"),
    );
};

export const getPolygonCoverage = async (polygon) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/polygon/coverage/`,
        "POST",
        polygon,
    ).then((res) => handleResponse(res, "Creating polygon coverage"));
};

export const postNewAsset = async (body) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/asset/`, "POST", body).then(
        (res) => handleResponse(res, "Creating a new asset"),
    );
};

export const getGaugeDefaultReturnPeriods = async (gaugeId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/gauges/${gaugeId}/gauge-default-return-periods`,
    ).then((res) => handleResponse(res, "Getting default gauge return periods"));
};

export const getVirtualGaugeDefaultReturnPeriods = async (gaugeId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/virtual-gauges/${gaugeId}/virtual-gauge-default-return-periods`,
    ).then((res) => handleResponse(res, "Getting default gauge return periods"));
};

// Edit Asset / Group Functions
export const postNewGroup = async (body) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/groups`, "POST", body).then(
        (res) => handleResponse(res, "Creating a new asset group"),
    );
};

export const putGroupName = async (id, body) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/groups/${id}`,
        "PUT",
        body,
    ).then((res) => handleResponse(res, "Editing an asset group"));
};

export const deleteGroup = async (id) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/groups/${id}`, "DELETE");
};

export const deleteAsset = async (id) => {
    return authFetch(`${env.VITE_BASE_API_URL}/admin/asset/${id}`, "DELETE");
};

export const putAssetDetails = async (id, body) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/asset/${id}`,
        "PUT",
        body,
    ).then((res) => handleResponse(res, "Editing an asset"));
};

export const getForecastDefaultReturnPeriods = async (id) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/assets/${id}/return-periods/default`,
    ).then((res) =>
        handleResponse(res, "Getting default forecast return periods for an asset"),
    );
};

export const putForecastReturnPeriods = async (id, body) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/asset/${id}/return-periods`,
        "PUT",
        body,
    ).then((res) => handleResponse(res, "Editing an asset's return periods"));
};

export const changeGaugeAssociation = async (
    assetId,
    gaugeId,
    body,
    method,
) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/asset/${assetId}/gauge/${gaugeId}`,
        method,
        body,
    ).then((res) =>
        handleResponse(
            res,
            "Modifying or Creating a gauge association to an asset",
        ),
    );
};

export const deleteGaugeAssociation = async (assetId) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/asset/${assetId}/gauge`,
        "DELETE",
    );
};

// Organization
export const getOrganizationHistoricalSubscriptions = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/organization/subscriptions`).then(
        (res) => res.json(),
    );
};

// Map Layers
export const getModelCoveragePolygons = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/layers/models`).then((res) =>
        res.json(),
    );
};

export const determineAssetGaugeWatershedOverlap = async (
    assetWatershed,
    gaugeId,
) => {
    // This must be a post request because it requires sending a JSON Payload
    // Sending a JSON Object in the URL could potentially overflow and exceed character limit
    return authFetch(
        `${env.VITE_BASE_API_URL}/gauges/${gaugeId}/overlap`,
        "POST",
        { assetWatershed: assetWatershed.geometry },
    ).then((res) =>
        handleResponse(res, "Determining Asset & Gauge Watershed Overlap"),
    );
};

export const determineAssetVirtualGaugeWatershedOverlap = async (
    assetWatershed,
    gaugeId,
) => {
    // This must be a post request because it requires sending a JSON Payload
    // Sending a JSON Object in the URL could potentially overflow and exceed character limit
    return authFetch(
        `${env.VITE_BASE_API_URL}/virtual-gauges/${gaugeId}/overlap`,
        "POST",
        { assetWatershed: assetWatershed.geometry },
    ).then((res) =>
        handleResponse(res, "Determining Asset & Gauge Watershed Overlap"),
    );
};

export const getAvailablePrecipTiles = async (model) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/layers/precip-tiles-available/${model}`,
    ).then((res) => res.json());
};

export const generateOrganizationApiKey = async () => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/organization/api-gateway-key`,
    ).then((res) => res.json());
};

export const checkOrganizationApiKey = async () => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/admin/organization/has-api-gateway-key`,
    ).then((res) => res.json());
};

// get all virtual and non-virtual gauges as a geojson object - roughly 16MB
// used in the gauges layer
export const getGaugesAsGeoJSON = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/layers/gauges/geojson`).then(
        (res) => res.json(),
    );
};

// get all configured virtual and non-virtual gauges as a geojson object - roughly 16MB
// used in the gauges layer
export const getConfiguredGaugesAsGeoJSON = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/layers/gauges/geojson/configured`).then(
        (res) => res.json(),
    );
};

// returns an array of search results for this specific query
export const getSearchResults = async (query) => {
    return authFetch(
        `${env.VITE_BASE_API_URL}/search/?${new URLSearchParams({ q: query })}`,
    ).then((res) => res.json());
};

export const getMaxFireYear = async () => {
    return authFetch(`${env.VITE_BASE_API_URL}/layers/max-fire-years-available`).then(
        (res) => res.json(),
    );
}